import type { TConversation } from '@cascade/api-client/conversations';
import { convertToCsv } from '@cascade/core/utils';
import { useConversations } from '@cascade/react/conversations';
import { downloadFile } from '@cascade/react-dom/utils';
import { useCallback, useState } from 'react';
import {
  buildTranscriptCsvRows,
  transcriptCsvColumns,
} from './useConversationsExport.helpers.ts';
import type {
  TConversationsExport,
  TConversationTranscript,
} from './useConversationsExport.types.ts';

const CSV_FILENAME = 'conversations.csv';
const CSV_TYPE = 'text/csv';

export const useConversationsExport = (): TConversationsExport => {
  const { getConversationMessages } = useConversations();
  const [isExporting, setIsExporting] = useState(false);

  const fetchTranscript = useCallback(
    async (conversation: TConversation): Promise<TConversationTranscript> => {
      const messages = await getConversationMessages(
        conversation.conversation_id,
      );

      return { conversation, messages };
    },
    [getConversationMessages],
  );

  const exportConversations = useCallback(
    async (conversations: TConversation[]) => {
      setIsExporting(true);

      try {
        const transcripts: TConversationTranscript[] = [];

        for (const conversation of conversations) {
          const transcript = await fetchTranscript(conversation);

          transcripts.push(transcript);
        }

        const rows = buildTranscriptCsvRows(transcripts);
        const content = convertToCsv(rows, {
          columns: transcriptCsvColumns,
          byteOrderMark: true,
        });

        downloadFile({ content, filename: CSV_FILENAME, type: CSV_TYPE });
      } finally {
        setIsExporting(false);
      }
    },
    [fetchTranscript],
  );

  return { isExporting, exportConversations };
};
